// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Run History Dialog for I-9 documents
 *
 * Shows every past processing run for a document, archived as re-runs superseded them.
 * Each row is one run's outcome (name, uid, match and which fields matched) plus the
 * reviewer's comment/fail type at the time. Read-only.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { History } from 'lucide-react'
import type { I9RunHistoryEntry } from './UserView'
import { StatusBadge } from './StatusBadge'
import { formatTimestamp } from './format'

interface HistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  docId: string | null
  history: I9RunHistoryEntry[]
}

// The four scored fields, shown as a compact badge row.
const MATCH_FIELDS = [
  { key: 'ssnMatch', label: 'SSN' },
  { key: 'firstNameMatch', label: 'First' },
  { key: 'lastNameMatch', label: 'Last' },
  { key: 'dobMatch', label: 'DOB' },
] as const

const MatchedFields: FC<{ entry: I9RunHistoryEntry }> = ({ entry }) => (
  <span className="flex flex-nowrap gap-1">
    {MATCH_FIELDS.map(({ key, label }) => (
      <span
        key={key}
        className={`px-1.5 py-0.5 rounded text-xs ${
          entry[key]
            ? 'bg-green-100 text-green-800'
            : 'bg-transparent text-muted-foreground/50 line-through border border-dashed border-muted-foreground/30'
        }`}
      >
        {label}
      </span>
    ))}
  </span>
)

export const HistoryDialog: FC<HistoryDialogProps> = ({ open, onOpenChange, docId, history }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-5xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Run history
        </DialogTitle>
        <DialogDescription>
          Past processing runs for <span className="font-mono">{docId}</span>, newest last.
          The current run is shown in the main table.
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[60vh] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Processed</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>UID</TableHead>
              <TableHead>Match</TableHead>
              <TableHead>Matched</TableHead>
              <TableHead>Crop</TableHead>
              <TableHead>Comment</TableHead>
              <TableHead>Fail Type</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((entry, i) => (
              <TableRow key={i}>
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {formatTimestamp(entry.processedAt)}
                </TableCell>
                <TableCell>{entry.status ? <StatusBadge status={entry.status} /> : '—'}</TableCell>
                <TableCell>{entry.empName ?? '—'}</TableCell>
                <TableCell className="font-mono text-muted-foreground">{entry.uid ?? '—'}</TableCell>
                <TableCell>{entry.matchScore}%</TableCell>
                <TableCell className="whitespace-nowrap">
                  <MatchedFields entry={entry} />
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {entry.cropTopFraction != null && entry.cropBottomFraction != null
                    ? `${entry.cropTopFraction}–${entry.cropBottomFraction}`
                    : 'default'}
                </TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground" title={entry.comment ?? undefined}>
                  {entry.comment || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">{entry.failType ?? '—'}</TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground" title={entry.error ?? undefined}>
                  {entry.error || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DialogContent>
  </Dialog>
)
